import { RenderApi, RenderFunction, CellContents, CellValue, PopulatedValue } from "./render";
import { formatter, Format, RGB } from "./format";
type BookInput<U> = (() => U)


type SheetInput<U> = {
    format?: Format
    name: string,
    width: number,
    height: number,
    _: (sheet?: SheetRef) => U
}

type ColumnInput = {
    id?: string,
    width?: number,
    format?: Format,
}

type RowInputFn<U> = (row: RowRef) => U
type RowInputObj<U> = {
    id?: string,
    height?: number,
    format?: Format,
    _?: RowInputFn<U>
}
type RowInput<U> = RowInputObj<U> | RowInputFn<U>
function rowInput<U = void>(input?: RowInput<U>): RowInputObj<U> {
    if (typeof input == 'function')
        return { _: input }
    else if (input)
        return input;
    else
        return {}
}

type CellInput = {
    col?: ColumnRef
    value?: PopulatedValue
    format?: Format
}
export type SheetRef = {

}
export type ColumnRef = {

}
export type RowRef = {

}
export type CellRef = {

}
type Sheet = SheetRef & {

}
type Column = ColumnRef & {
    sheet: Sheet
}
type Row = RowRef & {
    sheet: Sheet
}
type Cell = CellRef & {
    row: Row
}
type RootContext = {
    type: "root"
}
type BookContext = {
    type: "book"
}
type SheetContext = {
    type: "sheet"
    sheet: Sheet
    columnInput: {
        width: number
        id?: string
        freeze?: boolean
    }[]
    columns: Column[]
    columnFormats: (Format | undefined)[]
    firstRow: boolean
    frozenRow: boolean
    format: Format
    height: number
    width: number
}
type RowContext = {
    type: "row"
    format: Format
    columns: Column[]
    columnFormats: (Format | undefined)[]
    nextCI: number
}
type ContextType = 'root' | 'book' | 'sheet' | 'row'

type IsPromise<T> = T extends Promise<any> ? true : false;
// T is the render return type
// U is the content function return type
type CombinePromise<T, U> =
    IsPromise<T> extends true
    ? T
    : IsPromise<U> extends true
    ? Promise<T>
    : T;
// T is the reference object return type
// U is the content function return type
type VoidReturn<T, U> =
    U extends (void | undefined)
    ? T
    : U extends | (Promise<void> | Promise<undefined>)
    ? Promise<T>
    : U
type Context = RootContext | BookContext | SheetContext | RowContext
type TypedContext<T> = Context & { type: T }

export const parser =
    (() => {
        const { define, combine } = formatter();
        let api: RenderApi
        let currentContext: Context = { type: "root" }
        function withContext<T, U>(context: Context, _: () => U): VoidReturn<undefined, U>;
        function withContext<T, U>(context: Context, _: (ref: T) => U, ref: T): VoidReturn<T, U>;
        function withContext<T, U>(context: Context, _: (ref: T) => U, ref?: T): VoidReturn<T, U> {
            const prev = currentContext;
            currentContext = context;
            const finish = (obj: any) => {
                currentContext = prev;
                return obj === undefined ? ref : obj;
            }
            const obj = _(ref as T);
            return (obj instanceof Promise ? obj.then(finish) : finish(obj)) as VoidReturn<T, U>
        }
        function inContext<T extends ContextType, U>(type: T, context: Context): asserts context is TypedContext<T> {
            if (context.type !== type)
                throw new Error();
        }

        return {
            parse<T, U>(render: RenderFunction<T>, params: BookInput<U>): CombinePromise<T, U> {
                inContext('root', currentContext);
                const rapi = render();
                api = rapi
                const result = withContext({ type: "book" }, params)
                const finish = rapi.finish.bind(rapi);
                return (result instanceof Promise ? result.then(finish) : finish()) as CombinePromise<T, U>

            },
            sheet<U>({ name, format, height, width, _ }: SheetInput<U>) {
                inContext('book', currentContext);
                const { } = currentContext;
                api.addSheet({ name })
                const sheet: Sheet = {}
                return withContext({ type: "sheet", sheet, format: define(format || {}), height, width, columnInput: [], firstRow: true, columns: [], columnFormats: [], frozenRow: false }, _, sheet)
            },
            columns(...cols: ColumnInput[]): ColumnRef[] {
                inContext('sheet', currentContext);
                const { sheet, columnInput, columns, columnFormats, width: defaultWidth } = currentContext
                const added: Column[] = [];
                columnInput.push(...cols.map(({ id, width, format }) => {
                    columnFormats.push(format)
                    added.push({ sheet })
                    return {
                        id, width: width || defaultWidth
                    }
                }))
                columns.push(...added);
                return added;
            },
            freeze() {
                inContext('sheet', currentContext);
                const { firstRow, columnInput } = currentContext;
                if (firstRow) {
                    if (columnInput.length > 0)
                        columnInput[columnInput.length - 1].freeze = true;
                } else {
                    api.freezeRow()
                }

            },
            row<U>(input?: RowInput<U>): VoidReturn<RowRef, U> {
                inContext('sheet', currentContext);
                const { id, height, format, _ } = rowInput(input);
                const { firstRow, sheet, format: sheetFormat, height: defaultHeight, columnFormats, columns } = currentContext
                if (firstRow) {
                    api.setColumns(currentContext.columnInput)
                    currentContext.columnInput = [];
                    currentContext.firstRow = false;
                }

                api.addRow({ id, height: height || defaultHeight, format: combine(sheetFormat, format) })
                const row: Row = { sheet }
                if (_)
                    return withContext({ type: "row", format: combine(sheetFormat, format), columnFormats, columns, nextCI: 0 }, _, row)
                else
                    return row as unknown as VoidReturn<RowRef, U>
            },
            cell({ format, col, value }: CellInput) {
                inContext('row', currentContext);
                const { format: rowFormat, columnFormats, columns, nextCI } = currentContext
                const ci = col ? columns.indexOf((col as Column), nextCI) : nextCI
                if (ci < 0) throw new Error()
                currentContext.nextCI = ci + 1;
                const cellFormat = combine(combine(rowFormat, columnFormats[ci]), format);
                api.addCell({ ci, format: cellFormat, value })
                // deal with formula
            },
            RGB,
            defineFormat: define


        }
    })
